import { Database } from "supabase/database.types";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";

import { useAuth } from "@/components/AuthProvider";
import { API_BASE } from "@/constants/base-url";
import { createClient } from "@/lib/supabase";

interface UserProfileTable {
  Insert: Database["public"]["Tables"]["profiles"]["Insert"];
  Row: Database["public"]["Tables"]["profiles"]["Row"];
  Update: Database["public"]["Tables"]["profiles"]["Update"];
}

// Hook for updating display name
export function useUpdateDisplayName() {
  const { user } = useAuth();
  const supabase = createClient();

  return useSWRMutation<
    UserProfileTable["Row"],
    Error,
    string,
    { fullName: string }
  >(`${API_BASE}/profile/${user?.id}`, async (_url: string, { arg }) => {
    const { data } = await supabase
      .from("profiles")
      .update({ full_name: arg.fullName })
      .eq("user_id", user!.id)
      .select()
      .single()
      .throwOnError();

    return data;
  });
}

// Mutation hook for updating profile
export function useUpdateProfile() {
  const { user } = useAuth();
  const supabase = createClient();

  return useSWRMutation<
    null | UserProfileTable["Row"],
    Error,
    string,
    UserProfileTable["Update"]
  >(`${API_BASE}/profile/${user?.id}`, async (_url: string, { arg }) => {
    const { data, error } = await supabase
      .from("profiles")
      .update(arg)
      .eq("user_id", user!.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  });
}

export function useUserProfile() {
  const { user } = useAuth();
  const supabase = createClient();

  return useSWR<UserProfileTable["Row"]>(
    user ? `${API_BASE}/profile/${user.id}` : null,
    async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user!.id)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          // Profile doesn't exist, create one
          return await createProfile(user!.id);
        } else {
          throw error;
        }
      }

      return data;
    }
  );
}

// Helper function to create profile
async function createProfile(userId: string) {
  const supabase = createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) throw new Error("User not found");

  const { data } = await supabase
    .from("profiles")
    .upsert(
      {
        email: user.email || "",
        full_name: user.user_metadata?.full_name || "",
        preferred_difficulty: "intermediate",
        settings: {},
        user_id: userId,
      },
      {
        onConflict: "user_id",
      }
    )
    .select()
    .single()
    .throwOnError();

  return data;
}
